import { ArrowRight, User, Sparkles, Store, Palette, Flag } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import useTitle from "@/lib/useTitle";
import brand from "@/config/brand";

const VALUES = [
  {
    icon: User,
    title: "A Real Person, Every Step",
    text: "You'll work directly with the person designing your website — no ticket systems, no phone mazes, no getting lost in a queue.",
    id: "real-person",
  },
  {
    icon: Sparkles,
    title: "Simple & Stress-Free",
    text: "We handle the technical details and explain everything in plain English, so you always know what's happening and why.",
    id: "simple",
  },
  {
    icon: Store,
    title: "Built Around Your Business",
    text: "We take time to understand how your business works, who your customers are, and what your website actually needs to accomplish.",
    id: "business",
  },
  {
    icon: Palette,
    title: "Beautiful & Practical",
    text: "Great design isn't just pretty — it loads fast, works on every device, and makes it easy for customers to contact you.",
    id: "practical",
  },
];

const About = () => {
  useTitle(
    "About",
    "A boutique U.S.-based website design studio giving small businesses beautiful, practical websites with personal, stress-free service."
  );

  return (
    <main data-testid="about-page" className="bg-cream pt-[72px]">
      {/* Header */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-champagne/30 blur-[140px] pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-sans uppercase tracking-[0.28em] font-semibold text-turquoise-deep mb-6 flex items-center gap-3">
              <span className="w-10 h-px bg-turquoise inline-block" />
              About Us
            </p>
            <h1 className="font-serif font-medium tracking-tight text-charcoal text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
              A Website Partner Who{" "}
              <span className="italic text-turquoise">Actually Listens.</span>
            </h1>
            <p className="mt-6 text-lg font-sans text-taupe leading-relaxed max-w-2xl">
              {brand.name} is a boutique, U.S.-based website design studio serving small businesses
              nationwide. We believe every small business deserves a website that looks professional,
              works beautifully, and doesn't require a degree in technology to maintain.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Philosophy */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <h2 className="font-serif font-medium tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-tight">
                Why we <span className="italic text-turquoise">exist.</span>
              </h2>
              <div className="mt-6 space-y-5 font-sans text-base text-taupe leading-relaxed">
                <p>
                  Most small-business owners don't want to become website experts. They want a partner
                  they can trust — someone who understands their business, speaks plainly, and
                  delivers a website they're proud to share.
                </p>
                <p>
                  That's exactly what we do. We design, launch, host, and maintain beautiful websites
                  for small businesses, with personalized service and straightforward communication
                  from the first conversation to launch day and beyond.
                </p>
                <p className="flex items-start gap-3 text-charcoal/80">
                  <Flag size={18} className="text-turquoise-deep shrink-0 mt-1" />
                  <span>
                    We're proudly U.S.-based, and we work with the kinds of businesses that keep
                    America running — pressure washers, handymen, landscapers, painters, cleaners,
                    remodelers, and small businesses of every kind.
                  </span>
                </p>
              </div>
            </Reveal>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {VALUES.map((v, i) => (
                <Reveal key={v.id} delay={i * 0.08} className="h-full">
                  <div
                    data-testid={`about-value-${v.id}`}
                    className="h-full bg-cream-dark border border-taupe/15 rounded-2xl p-7 hover:border-turquoise/50 hover:-translate-y-1 transition-[border-color,transform] duration-300"
                  >
                    <div className="w-11 h-11 rounded-full bg-turquoise/10 border border-turquoise/30 flex items-center justify-center mb-5">
                      <v.icon size={19} className="text-turquoise-deep" strokeWidth={1.75} />
                    </div>
                    <h3 className="font-serif font-medium text-lg text-charcoal leading-snug mb-2">
                      {v.title}
                    </h3>
                    <p className="font-sans text-sm text-taupe leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Founder placeholder — editable */}
      <section data-testid="about-founder-section" className="relative bg-charcoal py-20 md:py-28 overflow-hidden">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-turquoise/10 blur-[150px] pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-5">
              <div
                data-testid="founder-photo-placeholder"
                className="aspect-[4/5] max-w-sm rounded-2xl border-2 border-dashed border-champagne/25 bg-ink2 flex flex-col items-center justify-center text-center p-8"
              >
                <User size={40} className="text-champagne/30 mb-4" strokeWidth={1.25} />
                <p className="font-serif text-lg text-champagne/60">Founder photo</p>
                <p className="font-sans text-xs text-champagne/40 mt-2">Coming soon</p>
              </div>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-7">
              <p className="text-xs font-sans uppercase tracking-[0.28em] font-semibold text-champagne mb-5 flex items-center gap-3">
                <span className="w-10 h-px bg-turquoise inline-block" />
                The Person Behind the Pixels
              </p>
              <h2 className="font-serif font-medium tracking-tight text-cream text-2xl sm:text-3xl lg:text-4xl leading-tight">
                Hi — I'm the person you'll <span className="italic text-turquoise">actually work with.</span>
              </h2>
              <p className="mt-6 font-sans text-base text-champagne/60 leading-relaxed max-w-xl">
                {brand.founderBlurb}
              </p>
              <p className="mt-4 font-sans text-sm text-champagne/45 leading-relaxed max-w-xl">
                When you reach out, you talk to me directly — from your first quote to launch day and
                every update after. No handoffs, no runaround.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif font-medium tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-tight">
              Let's build something <span className="italic text-turquoise">beautiful together.</span>
            </h2>
            <p className="mt-5 font-sans text-base text-taupe leading-relaxed max-w-xl mx-auto">
              Tell us about your business — we'll take it from there.
            </p>
            <Link
              data-testid="about-page-quote-button"
              to="/contact"
              className="group mt-10 inline-flex items-center gap-2 bg-turquoise text-charcoal hover:bg-turquoise-dark px-10 py-4 rounded-full font-sans font-semibold text-base shadow-[0_20px_40px_-12px_rgba(0,175,163,0.5)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5"
            >
              Request a Quote
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default About;
